(() => {
  'use strict';

  document.documentElement.classList.add('na-enhanced');

  const ready = new WeakSet();
  const revealReady = new WeakSet();
  const cleanups = new WeakMap();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const trapFocus = (container, event) => {
    if (event.key !== 'Tab') return;
    const focusable = [...container.querySelectorAll(focusableSelector)].filter(
      (element) => !element.hidden && element.getAttribute('tabindex') !== '-1' && element.offsetParent !== null
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const initialiseHeader = (header) => {
    if (ready.has(header)) return;
    ready.add(header);

    const toggle = header.querySelector('[data-na-nav-toggle]');
    const drawer = header.querySelector('[data-na-nav-drawer]');
    const drawerPanel = drawer?.querySelector('.na-mobile-nav__panel');
    const dismissers = header.querySelectorAll('[data-na-nav-dismiss]');
    const cartLink = header.querySelector('.na-header__cart');
    const cartCount = header.querySelector('.na-header__cart-count');
    const cartConfirmation = header.querySelector('[data-na-cart-confirmation]');
    const cartConfirmationCount = header.querySelector('[data-na-cart-confirmation-count]');
    const cartConfirmationNext = header.querySelector('[data-na-cart-confirmation-next]');
    const cartConfirmationSuggestion = header.querySelector('[data-na-cart-confirmation-suggestion]');
    const cartConfirmationRecommendations = header.querySelector('[data-na-cart-confirmation-recommendations]');
    const cartRecommendationHeading = cartConfirmationRecommendations?.querySelector('h3');
    const cartRecommendationCards = [...header.querySelectorAll('[data-na-recommendation-card]')];
    const cartRecommendationStatus = header.querySelector('[data-na-recommendation-status]');
    const governedOfferProducts = [...header.querySelectorAll('[data-na-offer-product]')];
    const cartConfirmationClosers = header.querySelectorAll('[data-na-cart-confirmation-close]');
    const cartConfirmationClose = header.querySelector('.na-cart-confirmation__close');
    const offersActive = cartConfirmation?.dataset.naOffersActive === 'true';
    let previouslyFocused;
    let cartConfirmationTrigger;
    let cartConfirmationTransitionTimer;
    let cartSyncSequence = 0;
    let cartUpdateUnsubscriber;
    let currentCartItemCount = Number(cartCount?.textContent || 0);
    let cartConfirmationDismissedCount = null;
    const isolatedElements = new Map();

    const isolateForDialog = (dialog, active) => {
      if (!dialog) return;
      if (!active) {
        isolatedElements.forEach((state, element) => {
          element.inert = state.inert;
          if (state.ariaHidden === null) element.removeAttribute('aria-hidden');
          else element.setAttribute('aria-hidden', state.ariaHidden);
        });
        isolatedElements.clear();
        return;
      }

      let current = dialog;
      while (current?.parentElement) {
        [...current.parentElement.children].forEach((sibling) => {
          if (sibling === current || sibling.contains(dialog) || isolatedElements.has(sibling)) return;
          isolatedElements.set(sibling, {
            inert: Boolean(sibling.inert),
            ariaHidden: sibling.getAttribute('aria-hidden')
          });
          sibling.inert = true;
          sibling.setAttribute('aria-hidden', 'true');
        });
        current = current.parentElement;
        if (current === document.body) break;
      }
    };

    const close = () => {
      if (!drawer || drawer.hidden) return;
      drawer.hidden = true;
      isolateForDialog(drawer, false);
      toggle?.setAttribute('aria-expanded', 'false');
      toggle?.setAttribute('aria-label', 'Open menu');
      document.documentElement.classList.remove('na-nav-is-open');
      const focusTarget = previouslyFocused?.isConnected ? previouslyFocused : toggle;
      focusTarget?.focus();
    };

    const open = () => {
      if (!drawer || !drawer.hidden) return;
      previouslyFocused = document.activeElement;
      drawer.hidden = false;
      isolateForDialog(drawer, true);
      toggle?.setAttribute('aria-expanded', 'true');
      toggle?.setAttribute('aria-label', 'Close menu');
      document.documentElement.classList.add('na-nav-is-open');
      requestAnimationFrame(() => drawerPanel?.querySelector(focusableSelector)?.focus());
    };

    toggle?.addEventListener('click', () => (drawer?.hidden ? open() : close()));
    dismissers.forEach((button) => button.addEventListener('click', close));
    drawer?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      trapFocus(drawer, event);
    });

    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    cleanups.set(header, () => {
      window.removeEventListener('scroll', onScroll);
      document.documentElement.classList.remove('na-nav-is-open');
    });

    header.querySelectorAll('details').forEach((details) => {
      details.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          details.removeAttribute('open');
          details.querySelector('summary')?.focus();
        }
      });
    });

    /* Desktop hover-intent navigation · 2026-08-05 */
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      header.querySelectorAll('details.na-nav-dropdown').forEach((details) => {
        let closeTimer;
        const openDropdown = () => {
          window.clearTimeout(closeTimer);
          header.querySelectorAll('details.na-nav-dropdown[open]').forEach((other) => {
            if (other !== details) other.removeAttribute('open');
          });
          details.setAttribute('open', '');
        };
        const closeDropdown = () => {
          window.clearTimeout(closeTimer);
          closeTimer = window.setTimeout(() => {
            if (!details.matches(':focus-within')) details.removeAttribute('open');
          }, 140);
        };
        details.addEventListener('pointerenter', openDropdown);
        details.addEventListener('pointerleave', closeDropdown);
        details.addEventListener('focusin', openDropdown);
        details.addEventListener('focusout', closeDropdown);
      });
    }

    const handleize = (value = '') => String(value)
      .trim()
      .toLowerCase()
      .replace(/&/g, '-')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

    const getCartLightingState = (cart) => {
      const cartItems = cart?.items || [];
      const governedRoles = new Set();

      if (governedOfferProducts.length) {
        cartItems.forEach((item) => {
          const itemVariantId = String(item.variant_id || item.id || '');
          governedOfferProducts.forEach((offerProduct) => {
            const handleMatches = offerProduct.dataset.naOfferHandle === item.handle;
            const variantMatches = String(offerProduct.dataset.naOfferVariant || '') === itemVariantId;
            if (!handleMatches || !variantMatches) return;
            String(offerProduct.dataset.naOfferRoles || '').split('|').filter(Boolean).forEach((role) => governedRoles.add(role));
          });
        });

        return {
          hasSpotlight: governedRoles.has('SPOTLIGHTS_A'),
          hasFog: governedRoles.has('FOG_B'),
          hasLedBar: governedRoles.has('LED_BARS_A'),
          hasWorkScene: governedRoles.has('WORK_SCENE_B')
        };
      }

      // Product type is only a resilience fallback when the governed product
      // manifest has not been configured in the theme editor.
      const productTypes = cartItems.map((item) => handleize(item.product_type));
      const hasType = (...needles) => productTypes.some((type) => needles.some((needle) => type.includes(needle)));

      return {
        hasSpotlight: hasType('spotlight', 'driving-light'),
        hasFog: hasType('fog-light'),
        hasLedBar: hasType('led-bar'),
        hasWorkScene: hasType('work-light', 'scene-light', 'work-scene')
      };
    };

    const getRecommendationRoles = ({ hasSpotlight, hasFog, hasLedBar, hasWorkScene }) => {
      const roles = [];

      if (hasSpotlight && !hasFog) roles.push('FOG_B');
      if (hasFog && !hasSpotlight) roles.push('SPOTLIGHTS_A');
      if (hasLedBar && !hasWorkScene) roles.push('WORK_SCENE_B');
      if (hasWorkScene && !hasLedBar) roles.push('LED_BARS_A');

      // Once a pair is complete, offer a useful entry point into the other
      // lighting system instead of showing a random catalogue product.
      if (!roles.length) {
        if (!hasSpotlight && !hasFog) roles.push('SPOTLIGHTS_A');
        if (!hasLedBar && !hasWorkScene) roles.push('LED_BARS_A');
      }

      return roles;
    };

    const updateRecommendations = (cart, lightingState) => {
      if (!cartConfirmationRecommendations || !cartRecommendationCards.length) return;

      const roles = getRecommendationRoles(lightingState);
      const cartHandles = new Set((cart?.items || []).map((item) => item.handle).filter(Boolean));
      const cartVariantIds = new Set((cart?.items || []).map((item) => String(item.variant_id || item.id || '')));
      const availableCards = cartRecommendationCards.filter((card) => {
        const alreadyInCart = cartHandles.has(card.dataset.naRecommendationHandle)
          || cartVariantIds.has(String(card.dataset.naRecommendationVariant || ''));
        return !alreadyInCart && roles.includes(card.dataset.naRecommendationRole);
      });
      const selectedCards = [];

      // Give each incomplete offer one useful product before using the second
      // slot for an alternative from the same role.
      roles.forEach((role) => {
        const firstForRole = availableCards.find((card) => (
          card.dataset.naRecommendationRole === role && !selectedCards.includes(card)
        ));
        if (firstForRole && selectedCards.length < 2) selectedCards.push(firstForRole);
      });

      availableCards.forEach((card) => {
        if (selectedCards.length < 2 && !selectedCards.includes(card)) selectedCards.push(card);
      });

      cartRecommendationCards.forEach((card) => {
        card.hidden = !selectedCards.includes(card);
      });
      cartConfirmationRecommendations.hidden = selectedCards.length === 0;

      if (cartRecommendationHeading) {
        const singleRoleHeadings = {
          FOG_B: 'Choose A Fog Light',
          SPOTLIGHTS_A: 'Choose A Spotlight',
          WORK_SCENE_B: 'Choose A Work Light',
          LED_BARS_A: 'Choose An LED Bar'
        };
        cartRecommendationHeading.textContent = roles.length === 1
          ? singleRoleHeadings[roles[0]] || 'Complete Your Setup'
          : 'Complete Your Offers';
      }
    };

    const updateCartState = (cart) => {
      const itemCount = Number(cart?.item_count || 0);
      if (itemCount < currentCartItemCount) cartConfirmationDismissedCount = null;
      currentCartItemCount = itemCount;
      if (cartCount) cartCount.textContent = String(itemCount);
      cartLink?.setAttribute('aria-label', `Cart with ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`);
      if (cartConfirmationCount) {
        cartConfirmationCount.textContent = `${itemCount} ${itemCount === 1 ? 'product' : 'products'} in cart`;
      }

      const lightingState = getCartLightingState(cart);
      const { hasSpotlight, hasFog, hasLedBar, hasWorkScene } = lightingState;
      updateRecommendations(cart, lightingState);

      if (cartConfirmationNext) {
        let suggestionLabel = 'Browse Matching Additions';
        let suggestionUrl = cartConfirmation?.dataset.naOffersUrl || '/collections/all';

        if (hasSpotlight && !hasFog) {
          cartConfirmationNext.textContent = 'Add a matching Fog Light to complete the Spotlight + Fog offer. Choose one below, then confirm fitment before checkout.';
          suggestionLabel = 'Browse Fog Lights';
          suggestionUrl = cartConfirmation?.dataset.naFogUrl || suggestionUrl;
        } else if (hasFog && !hasSpotlight) {
          cartConfirmationNext.textContent = 'Add a matching Spotlight or Driving Light to complete the pair. Choose one below, then confirm fitment before checkout.';
          suggestionLabel = 'Browse Spotlights';
          suggestionUrl = cartConfirmation?.dataset.naSpotlightUrl || suggestionUrl;
        } else if (hasLedBar && !hasWorkScene) {
          cartConfirmationNext.textContent = 'Add a matching Work or Scene Light to complete the LED Bar offer. Choose one below, then confirm fitment before checkout.';
          suggestionLabel = 'Browse Work & Scene Lights';
          suggestionUrl = cartConfirmation?.dataset.naWorkSceneUrl || suggestionUrl;
        } else if (hasWorkScene && !hasLedBar) {
          cartConfirmationNext.textContent = 'Add a matching LED Bar to complete the pair. Choose one below, then confirm fitment before checkout.';
          suggestionLabel = 'Browse LED Bars';
          suggestionUrl = cartConfirmation?.dataset.naLedBarUrl || suggestionUrl;
        } else if ((hasSpotlight && hasFog) || (hasLedBar && hasWorkScene)) {
          cartConfirmationNext.textContent = offersActive
            ? 'Your cart contains a complete product pair. Review your cart for progress and checkout will confirm the final saving.'
            : 'Your cart contains a complete product pair. Review the cart for its current offer status.';
          suggestionLabel = 'Explore Another Offer';
        } else {
          cartConfirmationNext.textContent = 'Choose a useful next product for the way you drive, or review the cart before checkout.';
        }

        if (cartConfirmationSuggestion) {
          cartConfirmationSuggestion.textContent = suggestionLabel;
          cartConfirmationSuggestion.href = suggestionUrl;
        }
      }

      return itemCount;
    };

    const hideCartConfirmation = ({ restoreFocus = false, markDismissed = restoreFocus } = {}) => {
      window.clearTimeout(cartConfirmationTransitionTimer);
      if (!cartConfirmation) return;
      if (markDismissed) cartConfirmationDismissedCount = currentCartItemCount;
      cartConfirmation.classList.remove('is-visible');
      document.documentElement.classList.remove('na-cart-confirmation-is-open');
      isolateForDialog(cartConfirmation, false);
      cartConfirmationTransitionTimer = window.setTimeout(() => {
        cartConfirmation.hidden = true;
      }, 180);
      const nativeCartDrawerIsOpen = document.querySelector('cart-drawer.active');
      if (restoreFocus && !nativeCartDrawerIsOpen && cartConfirmationTrigger?.isConnected) {
        cartConfirmationTrigger.focus();
      }
    };

    const showCartConfirmation = (itemCount = currentCartItemCount) => {
      if (!cartConfirmation) return;
      if (cartConfirmationDismissedCount === itemCount) return;
      if (!cartConfirmation.hidden && cartConfirmation.classList.contains('is-visible')) return;
      window.clearTimeout(cartConfirmationTransitionTimer);
      const nativeCartDrawer = document.querySelector('cart-drawer.active');
      if (typeof nativeCartDrawer?.close === 'function') nativeCartDrawer.close();
      if (cartRecommendationStatus) cartRecommendationStatus.textContent = '';
      cartConfirmation.hidden = false;
      isolateForDialog(cartConfirmation, true);
      document.documentElement.classList.add('na-cart-confirmation-is-open');
      cartConfirmation.classList.add('is-visible');
      requestAnimationFrame(() => cartConfirmationClose?.focus());
    };

    const onCartConfirmationKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        hideCartConfirmation({ restoreFocus: true });
        return;
      }
      trapFocus(cartConfirmation, event);
    };

    const syncCart = async ({ show = false, previousCount } = {}) => {
      const syncSequence = ++cartSyncSequence;
      const stateUrl = cartConfirmation?.dataset.naCartStateUrl || '/cart.js';
      try {
        const response = await fetch(stateUrl, {
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return;
        const cart = await response.json();
        if (syncSequence !== cartSyncSequence) return;
        const itemCount = updateCartState(cart);
        if (show && (previousCount === undefined || itemCount > previousCount)) showCartConfirmation(itemCount);
      } catch (error) {
        // The cart page remains the authoritative fallback if a background refresh fails.
      }
    };

    const onBlazeCartUpdated = (event) => {
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement && activeElement !== document.body) {
        cartConfirmationTrigger = activeElement;
        const nativeCartDrawer = document.querySelector('cart-drawer');
        if (event.detail?.source === 'complete-setup' && typeof nativeCartDrawer?.setActiveElement === 'function') {
          nativeCartDrawer.setActiveElement(activeElement);
        }
      }
      syncCart({ show: true });
    };
    const onProductFormSubmit = (event) => {
      const form = event.target.closest?.('form[action*="/cart/add"]');
      if (!form || form.closest('[data-blaze-complete-setup]') || form.matches('[data-na-recommendation-form]')) return;
      cartConfirmationTrigger = event.submitter || document.activeElement || form.querySelector('[type="submit"]');
      const previousCount = Number(cartCount?.textContent || 0);
      window.setTimeout(() => syncCart({ show: true, previousCount }), 1100);
    };

    const onRecommendationSubmit = async (event) => {
      const form = event.target.closest?.('[data-na-recommendation-form]');
      if (!form) return;

      event.preventDefault();
      const submittedCard = form.closest('[data-na-recommendation-card]');
      const submitButton = event.submitter || form.querySelector('[data-na-recommendation-submit]');
      const originalLabel = submitButton?.textContent || 'Add To Cart';
      cartConfirmationTrigger = submitButton;
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Adding…';
      }
      if (cartRecommendationStatus) cartRecommendationStatus.textContent = 'Adding this product to your cart…';

      try {
        const response = await fetch(`${form.action}.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: new FormData(form)
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.description || result.message || 'This product could not be added.');

        await syncCart();
        if (cartRecommendationStatus) {
          cartRecommendationStatus.textContent = 'Added. Your cart and offer progress are up to date.';
        }
        if (submittedCard?.hidden) {
          const nextRecommendation = cartRecommendationCards.find((card) => !card.hidden)?.querySelector(focusableSelector);
          const nextFocusTarget = nextRecommendation || cartConfirmation?.querySelector('.na-cart-confirmation__action--primary');
          requestAnimationFrame(() => nextFocusTarget?.focus());
        }
      } catch (error) {
        if (cartRecommendationStatus) {
          cartRecommendationStatus.textContent = error?.message || 'This product could not be added. Please open the product to choose an option.';
        }
      } finally {
        if (submitButton?.isConnected) {
          submitButton.disabled = false;
          submitButton.textContent = originalLabel;
        }
      }
    };

    const openNativeCartDrawer = (event) => {
      const nativeCartDrawer = document.querySelector('cart-drawer');
      if (!nativeCartDrawer || typeof nativeCartDrawer.open !== 'function') return;
      if (event.type === 'click' && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
      event.preventDefault();
      nativeCartDrawer.open(cartLink);
    };

    const onCartLinkKeydown = (event) => {
      if (event.key !== ' ' && event.code !== 'Space') return;
      openNativeCartDrawer(event);
    };

    document.addEventListener('blaze:cart-updated', onBlazeCartUpdated);
    document.addEventListener('submit', onProductFormSubmit, true);
    cartLink?.addEventListener('click', openNativeCartDrawer);
    cartLink?.addEventListener('keydown', onCartLinkKeydown);
    cartConfirmationClosers.forEach((closer) => closer.addEventListener('click', () => hideCartConfirmation({ restoreFocus: true })));
    cartConfirmation?.addEventListener('keydown', onCartConfirmationKeydown);
    cartConfirmation?.addEventListener('submit', onRecommendationSubmit);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        syncCart({ show: event?.source === 'product-form' });
      });
    }

    const previousCleanup = cleanups.get(header);
    cleanups.set(header, () => {
      previousCleanup?.();
      window.clearTimeout(cartConfirmationTransitionTimer);
      document.documentElement.classList.remove('na-cart-confirmation-is-open');
      isolateForDialog(drawer, false);
      document.removeEventListener('blaze:cart-updated', onBlazeCartUpdated);
      document.removeEventListener('submit', onProductFormSubmit, true);
      cartLink?.removeEventListener('click', openNativeCartDrawer);
      cartLink?.removeEventListener('keydown', onCartLinkKeydown);
      cartConfirmation?.removeEventListener('keydown', onCartConfirmationKeydown);
      cartConfirmation?.removeEventListener('submit', onRecommendationSubmit);
      if (typeof cartUpdateUnsubscriber === 'function') cartUpdateUnsubscriber();
    });
  };

  const initialiseMap = (map) => {
    if (ready.has(map)) return;
    ready.add(map);

    const tabs = [...map.querySelectorAll('[role="tab"]')];
    const panels = [...map.querySelectorAll('[role="tabpanel"]')];
    if (!tabs.length || !panels.length) return;

    const activate = (tab, moveFocus = false) => {
      const panelId = tab.getAttribute('aria-controls');
      tabs.forEach((candidate) => {
        const active = candidate === tab;
        candidate.setAttribute('aria-selected', active ? 'true' : 'false');
        candidate.tabIndex = active ? 0 : -1;
        candidate.classList.toggle('is-active', active);
      });
      panels.forEach((panel) => {
        const active = panel.id === panelId;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
      map.dataset.activeZone = tab.dataset.zone || '';
      if (moveFocus) tab.focus();
    };

    map.classList.add('is-enhanced');
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', (event) => {
        let nextIndex;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (nextIndex === undefined) return;
        event.preventDefault();
        activate(tabs[nextIndex], true);
      });
    });

    activate(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
  };

  const initialiseHero = (hero) => {
    if (ready.has(hero)) return;
    ready.add(hero);
    if (reduceMotion.matches || !finePointer.matches) return;

    hero.addEventListener('pointermove', (event) => {
      const bounds = hero.getBoundingClientRect();
      const x = (event.clientX - bounds.left) / bounds.width - 0.5;
      const y = (event.clientY - bounds.top) / bounds.height - 0.5;
      hero.style.setProperty('--na-pointer-x', x.toFixed(3));
      hero.style.setProperty('--na-pointer-y', y.toFixed(3));
    });
    hero.addEventListener('pointerleave', () => {
      hero.style.setProperty('--na-pointer-x', '0');
      hero.style.setProperty('--na-pointer-y', '0');
    });
  };

  const initialiseTilt = (card) => {
    if (ready.has(card)) return;
    ready.add(card);
    if (reduceMotion.matches || !finePointer.matches) return;

    const reset = () => {
      card.style.setProperty('--na-tilt-x', '0deg');
      card.style.setProperty('--na-tilt-y', '0deg');
      card.style.setProperty('--na-glow-x', '50%');
      card.style.setProperty('--na-glow-y', '50%');
    };

    card.addEventListener('pointermove', (event) => {
      const bounds = card.getBoundingClientRect();
      const x = (event.clientX - bounds.left) / bounds.width;
      const y = (event.clientY - bounds.top) / bounds.height;
      card.style.setProperty('--na-tilt-x', `${((0.5 - y) * 5).toFixed(2)}deg`);
      card.style.setProperty('--na-tilt-y', `${((x - 0.5) * 5).toFixed(2)}deg`);
      card.style.setProperty('--na-glow-x', `${(x * 100).toFixed(1)}%`);
      card.style.setProperty('--na-glow-y', `${(y * 100).toFixed(1)}%`);
    });
    card.addEventListener('pointerleave', reset);
    card.addEventListener('blur', reset, true);
  };

  const initialiseReveals = (root) => {
    const elements = [...root.querySelectorAll('[data-na-reveal]')].filter((element) => !revealReady.has(element));
    if (!elements.length) return;

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      elements.forEach((element) => {
        revealReady.add(element);
        element.classList.add('is-visible');
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
    );

    document.documentElement.classList.add('na-reveal-ready');

    elements.forEach((element) => {
      revealReady.add(element);
      observer.observe(element);
    });
  };

  const initialise = (root = document) => {
    root.querySelectorAll('[data-na-header]').forEach(initialiseHeader);
    root.querySelectorAll('[data-na-map]').forEach(initialiseMap);
    root.querySelectorAll('[data-na-hero]').forEach(initialiseHero);
    root.querySelectorAll('[data-na-tilt]').forEach(initialiseTilt);
    initialiseReveals(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-na-header]').forEach((header) => cleanups.get(header)?.());
  });
})();

/* Night Atlas commercial correction pass — 2026-08-05 */
(() => {
  "use strict";

  const CART_URL = "/cart.js";
  const THRESHOLD_CENTS = 750000;
  const money = (cents) =>
    `R ${(Number(cents || 0) / 100).toLocaleString("en-ZA", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const normalise = (value = "") =>
    String(value).replace(/\s+/g, " ").trim().toLowerCase();

  const offerRulesActive = () =>
    [...document.querySelectorAll("[data-blaze-offers-active], [data-na-offers-active]")].some(
      (element) =>
        element.dataset.blazeOffersActive === "true" ||
        element.dataset.naOffersActive === "true",
    );

  const offerManifest = () => {
    const byVariant = new Map();

    document.querySelectorAll("[data-na-offer-product]").forEach((entry) => {
      const roles = String(entry.dataset.naOfferRoles || "")
        .split("|")
        .filter(Boolean);
      const variant = String(entry.dataset.naOfferVariant || "");

      if (variant) {
        const roleSet = byVariant.get(variant) || new Set();
        roles.forEach((role) => roleSet.add(role));
        byVariant.set(variant, roleSet);
      }

    });

    return { byVariant };
  };

  const cartOfferState = (cart) => {
    const manifest = offerManifest();
    const roles = new Set();
    let eligibleSubtotal = 0;

    (cart?.items || []).forEach((item) => {
      const itemRoles = manifest.byVariant.get(
        String(item.variant_id || item.id || ""),
      );

      if (!itemRoles) return;
      itemRoles.forEach((role) => roles.add(role));
      if (itemRoles.has("Q3-01")) {
        eligibleSubtotal += Number(
          item.original_line_price ?? item.final_line_price ?? item.line_price ?? 0,
        );
      }
    });

    const state = {
      eligibleSubtotal,
      offersActive: offerRulesActive(),
      hasSpotlight: roles.has("SPOTLIGHTS_A"),
      hasFog: roles.has("FOG_B"),
      hasLedBar: roles.has("LED_BARS_A"),
      hasWorkScene: roles.has("WORK_SCENE_B"),
    };

    state.thresholdComplete = eligibleSubtotal >= THRESHOLD_CENTS;
    state.spotlightFogComplete = state.hasSpotlight && state.hasFog;
    state.ledWorkComplete = state.hasLedBar && state.hasWorkScene;
    state.allPairOffersComplete =
      state.spotlightFogComplete && state.ledWorkComplete;

    return state;
  };

  const missingRecommendationRoles = (state) => {
    const roles = [];
    if (state.hasSpotlight && !state.hasFog) roles.push("FOG_B");
    if (state.hasFog && !state.hasSpotlight) roles.push("SPOTLIGHTS_A");
    if (state.hasLedBar && !state.hasWorkScene) roles.push("WORK_SCENE_B");
    if (state.hasWorkScene && !state.hasLedBar) roles.push("LED_BARS_A");
    return roles;
  };

  const recommendationValue = (state, role) => {
    if (!state.offersActive) {
      return "Available match · checkout confirms current offer availability";
    }
    if (state.hasSpotlight && !state.hasFog && role === "FOG_B") {
      return "Complete the pair · 10% off this eligible Fog Light";
    }
    if (state.hasFog && !state.hasSpotlight && role === "SPOTLIGHTS_A") {
      return "Add the missing qualifier · 10% off your eligible Fog Light";
    }
    if (state.hasLedBar && !state.hasWorkScene && role === "WORK_SCENE_B") {
      return "Complete the pair · 10% off this eligible Work or Scene Light";
    }
    if (state.hasWorkScene && !state.hasLedBar && role === "LED_BARS_A") {
      return "Add the missing qualifier · 10% off your eligible Work or Scene Light";
    }
    if (state.allPairOffersComplete) {
      return "Available now · useful next upgrade";
    }
    if (!state.thresholdComplete) {
      return "Eligible addition · counts toward the R7,500 route";
    }
    return "Available now · add another useful lighting zone";
  };

  const rankedStaticRecommendations = (cart, state, limit = 3) => {
    const cartHandles = new Set(
      (cart?.items || []).map((item) => String(item.handle || "")),
    );
    const cartVariants = new Set(
      (cart?.items || []).map((item) =>
        String(item.variant_id || item.id || ""),
      ),
    );
    const neededRoles = missingRecommendationRoles(state);
    const seenHandles = new Set();

    return [...document.querySelectorAll("[data-na-recommendation-card]")]
      .filter((card) => {
        const handle = String(card.dataset.naRecommendationHandle || "");
        const variant = String(card.dataset.naRecommendationVariant || "");
        return !cartHandles.has(handle) && !cartVariants.has(variant);
      })
      .map((card, index) => {
        const role = String(card.dataset.naRecommendationRole || "");
        const neededIndex = neededRoles.indexOf(role);
        const score = neededIndex >= 0 ? 100 - neededIndex : state.allPairOffersComplete ? 30 : 10;
        return { card, role, score, index };
      })
      .sort((a, b) => b.score - a.score || a.index - b.index)
      .filter(({ card }) => {
        const handle = String(card.dataset.naRecommendationHandle || "");
        if (!handle || seenHandles.has(handle)) return false;
        seenHandles.add(handle);
        return true;
      })
      .slice(0, limit);
  };

  const popupDealCopy = (state) => {
    if (!state.offersActive) {
      return "This is an available product match for the lighting setup. Review the cart and checkout to confirm any current offer.";
    }
    if (state.hasSpotlight && !state.hasFog) {
      return "Your Spotlight route is started. Add one eligible Fog Light before checkout and that Fog Light receives 10% off automatically.";
    }
    if (state.hasFog && !state.hasSpotlight) {
      return "Your eligible Fog Light is waiting for its match. Add an eligible Spotlight or Driving Light and checkout applies 10% to one eligible Fog Light.";
    }
    if (state.hasLedBar && !state.hasWorkScene) {
      return "Your LED Bar route is started. Add one eligible Work or Scene Light before checkout and that matched light receives 10% off automatically.";
    }
    if (state.hasWorkScene && !state.hasLedBar) {
      return "Your eligible Work or Scene Light is waiting for its match. Add an eligible LED Bar and checkout applies 10% to one matched light.";
    }
    if (state.allPairOffersComplete) {
      return "Both pairing offers are already complete. These available products add another useful lighting zone; checkout still compares every valid saving.";
    }
    if (!state.thresholdComplete) {
      return "Choose a useful eligible addition while this build is open. Your cart page shows exactly how much remains to reach the R7,500 route.";
    }
    return "Your R7,500 eligible total is reached. Add only what improves the setup; checkout compares the R600 route with every valid pair saving.";
  };

  const buildPopupCard = (product) => {
    const variant = (product.variants || []).find((item) => item.available) ||
      product.variants?.[0];
    if (!variant) return null;
    const canQuickAdd =
      product.available &&
      Array.isArray(product.variants) &&
      product.variants.length === 1 &&
      variant.available;

    const article = document.createElement("article");
    article.className = "na-cart-confirmation__recommendation";
    article.setAttribute("role", "listitem");
    article.dataset.naRecommendationCard = "";
    article.dataset.naRecommendationRole = "ADJACENT";
    article.dataset.naRecommendationHandle = product.handle;
    article.dataset.naRecommendationVariant = String(variant.id);

    const media = document.createElement("a");
    media.className = "na-cart-confirmation__recommendation-media";
    media.href = product.url || `/products/${product.handle}`;
    media.tabIndex = -1;
    media.setAttribute("aria-hidden", "true");
    if (product.featured_image) {
      const image = document.createElement("img");
      image.src = product.featured_image;
      image.alt = product.title || "Recommended product";
      image.loading = "lazy";
      media.append(image);
    }

    const body = document.createElement("div");
    body.className = "na-cart-confirmation__recommendation-body";
    const value = document.createElement("span");
    value.textContent = "Available now · useful product match";
    const title = document.createElement("h4");
    const link = document.createElement("a");
    link.href = product.url || `/products/${product.handle}`;
    link.textContent = product.title;
    title.append(link);
    const price = document.createElement("strong");
    price.textContent = money(variant.price ?? product.price ?? 0);
    let action;
    if (canQuickAdd) {
      const form = document.createElement("form");
      form.method = "post";
      form.action = "/cart/add";
      form.dataset.naRecommendationForm = "";
      const id = document.createElement("input");
      id.type = "hidden";
      id.name = "id";
      id.value = String(variant.id);
      const quantity = document.createElement("input");
      quantity.type = "hidden";
      quantity.name = "quantity";
      quantity.value = "1";
      const button = document.createElement("button");
      button.type = "submit";
      button.dataset.naRecommendationSubmit = "";
      button.textContent = "Add To Cart";
      form.append(id, quantity, button);
      action = form;
    } else {
      action = document.createElement("a");
      action.className = "na-cart-confirmation__choose-options";
      action.href = product.url || `/products/${product.handle}`;
      action.textContent = "Choose Options";
    }
    body.append(value, title, price, action);
    article.append(media, body);
    return article;
  };

  const dynamicRecommendations = async (cart, limit = 2) => {
    const lastProductId = [...(cart?.items || [])]
      .reverse()
      .find((item) => item.product_id)?.product_id;
    if (!lastProductId) return [];

    const cartHandles = new Set(
      (cart?.items || []).map((item) => String(item.handle || "")),
    );

    try {
      const response = await fetch(
        `/recommendations/products.json?product_id=${encodeURIComponent(lastProductId)}&limit=8&intent=related`,
        { cache: "no-store", headers: { Accept: "application/json" } },
      );
      if (!response.ok) return [];
      const payload = await response.json();
      return (payload.products || [])
        .filter(
          (product) =>
            product.available &&
            !cartHandles.has(String(product.handle || "")),
        )
        .slice(0, limit);
    } catch {
      return [];
    }
  };

  const ensurePopupRecommendations = async (cart, state) => {
    const panel = document.querySelector("[data-na-cart-confirmation]");
    const section = panel?.querySelector(
      "[data-na-cart-confirmation-recommendations]",
    );
    const grid = section?.querySelector(
      ".na-cart-confirmation__recommendation-grid",
    );
    if (!panel || !section || !grid) return;

    let ranked = rankedStaticRecommendations(cart, state, 2);
    let selectedCards = ranked.map(({ card }) => card);

    if (!selectedCards.length) {
      const products = await dynamicRecommendations(cart, 2);
      const generated = products.map(buildPopupCard).filter(Boolean);
      generated.forEach((card) => grid.append(card));
      selectedCards = generated;
      ranked = generated.map((card) => ({
        card,
        role: "ADJACENT",
      }));
    }

    const allCards = [...grid.querySelectorAll("[data-na-recommendation-card]")];
    allCards.forEach((card) => {
      card.hidden = !selectedCards.includes(card);
    });

    if (!selectedCards.length) {
      section.hidden = true;
      if (!panel.hidden || panel.classList.contains("is-visible")) {
        window.location.assign("/cart");
      }
      return;
    }

    ranked.forEach(({ card, role }) => {
      const value = card.querySelector(
        ".na-cart-confirmation__recommendation-body > span",
      );
      if (value) value.textContent = recommendationValue(state, role);
    });

    section.hidden = false;
    const headingWrap = section.querySelector(
      ".na-cart-confirmation__recommendations-heading",
    );
    const kicker = headingWrap?.querySelector("span");
    const heading = headingWrap?.querySelector("h3");
    if (kicker) kicker.textContent = "A Useful Next Product";
    if (heading) {
      heading.textContent = state.allPairOffersComplete
        ? "Add The Next Lighting Zone"
        : missingRecommendationRoles(state).length
          ? "Complete The Offer Already In Your Cart"
          : "Choose A Product That Builds The Setup";
    }

    let dealNote = section.querySelector(".na-cart-confirmation__deal-note");
    if (!dealNote) {
      dealNote = document.createElement("p");
      dealNote.className = "na-cart-confirmation__deal-note";
      headingWrap?.insertAdjacentElement("afterend", dealNote);
    }
    dealNote.textContent = popupDealCopy(state);

    const next = panel.querySelector("[data-na-cart-confirmation-next]");
    if (next) {
      next.textContent = state.allPairOffersComplete
        ? "Your offer pairs are complete. Choose an adjacent upgrade below or review every saving on the cart page."
        : "Choose the matching product below or open the cart page to see every offer bar.";
    }
  };

  const makeOfferTrack = ({ title, badge, value, copy, href, link, complete }) => {
    const article = document.createElement("article");
    article.className = `na-cart-offer-track${complete ? " is-complete" : ""}`;

    const top = document.createElement("div");
    top.className = "na-cart-offer-track__top";
    const heading = document.createElement("strong");
    heading.textContent = title;
    const status = document.createElement("span");
    status.textContent = badge;
    top.append(heading, status);

    const progress = document.createElement("progress");
    progress.max = 100;
    progress.value = Math.max(0, Math.min(100, Number(value || 0)));
    progress.setAttribute("aria-label", `${title}: ${Math.round(progress.value)}% complete`);

    const description = document.createElement("p");
    description.textContent = copy;
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.textContent = link;
    article.append(top, progress, description, anchor);
    return article;
  };

  const renderOfferDashboard = (cart, state) => {
    const root = document.querySelector(".blaze-cart-offers__inner");
    if (!root) return;

    const thresholdPercent = Math.min(
      100,
      Math.round((state.eligibleSubtotal / THRESHOLD_CENTS) * 100),
    );
    const thresholdRemaining = Math.max(
      0,
      THRESHOLD_CENTS - state.eligibleSubtotal,
    );
    const spotlightFogPercent =
      (state.hasSpotlight ? 50 : 0) + (state.hasFog ? 50 : 0);
    const ledWorkPercent =
      (state.hasLedBar ? 50 : 0) + (state.hasWorkScene ? 50 : 0);

    const thresholdCopy = state.thresholdComplete
      ? state.offersActive
        ? "Eligible total reached. Checkout compares the R600 route with any valid pair savings and applies the best combination."
        : "Eligible total reached. Review checkout to confirm the offers currently available for this cart."
      : state.offersActive
        ? `${money(thresholdRemaining)} more in eligible, in-stock, full-price products reaches the R600 route.`
        : `This cart is ${money(thresholdRemaining)} below the eligible R7,500 total. Checkout confirms current offers.`;
    const spotlightFogCopy = state.spotlightFogComplete
      ? state.offersActive
        ? "Pair complete. Checkout confirms 10% off one eligible Fog Light."
        : "Eligible pair complete. Review checkout to confirm the current offer."
      : state.hasSpotlight
        ? "Spotlight or Driving Light added. Choose one eligible Fog Light to complete the pair."
        : state.hasFog
          ? "Fog Light added. Choose an eligible Spotlight or Driving Light to complete the pair."
          : "Add an eligible Spotlight or Driving Light plus one eligible Fog Light.";
    const ledWorkCopy = state.ledWorkComplete
      ? state.offersActive
        ? "Pair complete. Checkout confirms 10% off one eligible Work or Scene Light."
        : "Eligible pair complete. Review checkout to confirm the current offer."
      : state.hasLedBar
        ? "LED Bar added. Choose one eligible Work or Scene Light to complete the pair."
        : state.hasWorkScene
          ? "Work or Scene Light added. Choose one eligible LED Bar to complete the pair."
          : "Add an eligible LED Bar plus one eligible Work or Scene Light.";

    let dashboard = root.querySelector(".na-cart-offer-dashboard");
    if (!dashboard) {
      dashboard = document.createElement("div");
      dashboard.className = "na-cart-offer-dashboard";
      dashboard.setAttribute("aria-label", "Offer progress");
      const header = root.querySelector(".blaze-cart-offers__header");
      header?.insertAdjacentElement("afterend", dashboard);
    }

    dashboard.replaceChildren(
      makeOfferTrack({
        title: "R600 Full-System Saving",
        badge: state.thresholdComplete
          ? state.offersActive
            ? "ROUTE READY"
            : "CHECK AT CHECKOUT"
          : `${thresholdPercent}%`,
        value: thresholdPercent,
        copy: thresholdCopy,
        href: "/collections/all?view=offers",
        link: state.thresholdComplete ? "Review eligible products" : "Build the R7,500 offer",
        complete: state.thresholdComplete,
      }),
      makeOfferTrack({
        title: "Spotlight + Fog",
        badge: state.spotlightFogComplete
          ? state.offersActive
            ? "10% READY"
            : "PAIR READY"
          : `${spotlightFogPercent}%`,
        value: spotlightFogPercent,
        copy: spotlightFogCopy,
        href: state.hasFog && !state.hasSpotlight ? "/collections/spotlights" : "/collections/fog-lights",
        link: state.spotlightFogComplete ? "Review the pair" : state.hasFog ? "Choose a Spotlight" : "Choose a Fog Light",
        complete: state.spotlightFogComplete,
      }),
      makeOfferTrack({
        title: "LED Bar + Work / Scene",
        badge: state.ledWorkComplete
          ? state.offersActive
            ? "10% READY"
            : "PAIR READY"
          : `${ledWorkPercent}%`,
        value: ledWorkPercent,
        copy: ledWorkCopy,
        href: state.hasWorkScene && !state.hasLedBar ? "/collections/led-bar" : "/collections/working-lights",
        link: state.ledWorkComplete ? "Review the pair" : state.hasWorkScene ? "Choose an LED Bar" : "Choose a Work / Scene Light",
        complete: state.ledWorkComplete,
      }),
    );

    let liveUpdate = root.querySelector(".na-cart-offer-dashboard-status");
    if (!liveUpdate) {
      liveUpdate = document.createElement("p");
      liveUpdate.className = "na-cart-offer-dashboard-status visually-hidden";
      liveUpdate.setAttribute("role", "status");
      liveUpdate.setAttribute("aria-live", "polite");
      liveUpdate.setAttribute("aria-atomic", "true");
      dashboard.insertAdjacentElement("afterend", liveUpdate);
    }
    const completeRoutes = [
      state.thresholdComplete,
      state.spotlightFogComplete,
      state.ledWorkComplete,
    ].filter(Boolean).length;
    liveUpdate.textContent = `${completeRoutes} of 3 offer routes are ready for checkout review.`;

    const eyebrow = root.querySelector(".blaze-cart-offers__eyebrow");
    const heading = root.querySelector(".blaze-cart-offers__header h2");
    const status = root.querySelector(".blaze-cart-offers__status");
    if (eyebrow) {
      const itemCount = Number(cart?.item_count || 0);
      eyebrow.textContent = `Live Offer Progress · ${itemCount} ${itemCount === 1 ? "Item" : "Items"}`;
    }
    if (heading) heading.textContent = "Your Cart. Every Offer Explained.";
    if (status) {
      status.textContent = state.offersActive
        ? "Checkout confirms the best valid saving automatically"
        : "Checkout confirms any offer currently available";
    }

    const estimate = root.querySelector(".blaze-cart-offers__estimate");
    const estimateAmount = estimate?.querySelector("div strong");
    const estimateCopy = estimate?.querySelector(":scope > p");
    const appliedSaving = state.offersActive ? Number(cart?.total_discount || 0) : 0;
    if (estimateAmount) estimateAmount.textContent = money(appliedSaving);
    if (estimateCopy) {
      estimateCopy.textContent = state.offersActive
        ? appliedSaving > 0
          ? "The adjusted line prices above show where Shopify applied the best valid automatic offer combination."
          : "No saving is complete yet. Use the relevant next step below to finish an eligible pair or reach the full-system threshold."
        : "Automatic savings are temporarily unavailable.";
    }
  };

  const cartUpsellCard = (source, state) => {
    const role = String(source.dataset.naRecommendationRole || "");
    const article = document.createElement("article");
    article.className = "na-cart-upsell-card";

    const sourceMedia = source.querySelector(
      ".na-cart-confirmation__recommendation-media",
    );
    const media = sourceMedia?.cloneNode(true) || document.createElement("a");
    media.className = "na-cart-upsell-card__media";

    const body = document.createElement("div");
    body.className = "na-cart-upsell-card__body";
    const value = document.createElement("span");
    value.textContent = recommendationValue(state, role);

    const sourceTitle = source.querySelector(
      ".na-cart-confirmation__recommendation-body h4 a",
    );
    const title = document.createElement("h4");
    const link = sourceTitle?.cloneNode(true) || document.createElement("a");
    if (!link.textContent) link.textContent = "View Product";
    title.append(link);

    const price = document.createElement("strong");
    price.textContent =
      source.querySelector(".na-cart-confirmation__recommendation-body > strong")
        ?.textContent?.trim() || "View price";

    let action;
    if (source.querySelector("form[data-na-recommendation-form]")) {
      const form = document.createElement("form");
      form.method = "post";
      form.action = "/cart/add";
      form.dataset.naCartUpsellForm = "";
      form.dataset.naRecommendationForm = "";
      const id = document.createElement("input");
      id.type = "hidden";
      id.name = "id";
      id.value = String(source.dataset.naRecommendationVariant || "");
      const quantity = document.createElement("input");
      quantity.type = "hidden";
      quantity.name = "quantity";
      quantity.value = "1";
      const button = document.createElement("button");
      button.type = "submit";
      button.textContent = "Add To Cart";
      form.append(id, quantity, button);
      action = form;
    } else {
      action = link.cloneNode(true);
      action.className = "na-cart-upsell-card__choose-options";
      action.textContent = "Choose Options";
    }
    body.append(value, title, price, action);
    article.append(media, body);
    return article;
  };

  const cartUpsellFromProduct = (product, state) => {
    const popupCard = buildPopupCard(product);
    if (!popupCard) return null;

    const article = document.createElement("article");
    article.className = "na-cart-upsell-card";
    const media = popupCard.querySelector(
      ".na-cart-confirmation__recommendation-media",
    )?.cloneNode(true);
    if (media) media.className = "na-cart-upsell-card__media";

    const body = document.createElement("div");
    body.className = "na-cart-upsell-card__body";
    const value = document.createElement("span");
    value.textContent = recommendationValue(state, "ADJACENT");
    const title = document.createElement("h4");
    const sourceTitle = popupCard.querySelector(
      ".na-cart-confirmation__recommendation-body h4 a",
    );
    if (sourceTitle) title.append(sourceTitle.cloneNode(true));
    const price = popupCard.querySelector(
      ".na-cart-confirmation__recommendation-body > strong",
    )?.cloneNode(true);
    const sourceAction = popupCard.querySelector(
      ".na-cart-confirmation__recommendation-body > form, .na-cart-confirmation__choose-options",
    );
    let action = sourceAction?.cloneNode(true);
    if (action?.tagName === "FORM") {
      action.dataset.naCartUpsellForm = "";
      action.dataset.naRecommendationForm = "";
    } else if (action) {
      action.className = "na-cart-upsell-card__choose-options";
    }
    body.append(value, title);
    if (price) body.append(price);
    if (action) body.append(action);
    article.append(media || document.createElement("span"), body);
    return article;
  };

  const renderCartUpsells = async (cart, state) => {
    const root = document.querySelector(".blaze-cart-offers__inner");
    if (!root) return;
    let section = root.querySelector(".blaze-cart-offers__recommendations");
    if (!section) {
      section = document.createElement("section");
      section.className = "blaze-cart-offers__recommendations";
      const intro = document.createElement("div");
      const eyebrow = document.createElement("p");
      eyebrow.className = "blaze-cart-offers__eyebrow";
      const heading = document.createElement("h3");
      const description = document.createElement("p");
      intro.append(eyebrow, heading, description);
      const recommendationGrid = document.createElement("div");
      recommendationGrid.className = "blaze-cart-offers__recommendation-grid";
      section.append(intro, recommendationGrid);
      root.append(section);
    }
    const grid = section.querySelector(".blaze-cart-offers__recommendation-grid");
    if (!grid) return;

    const ranked = rankedStaticRecommendations(cart, state, 3);
    let cards = ranked.map(({ card }) => cartUpsellCard(card, state));
    if (!cards.length) {
      const products = await dynamicRecommendations(cart, 3);
      cards = products.map((product) => cartUpsellFromProduct(product, state)).filter(Boolean);
    }
    if (!cards.length) {
      section.hidden = true;
      grid.replaceChildren();
      return;
    }
    section.hidden = false;
    grid.replaceChildren(...cards);
    grid.dataset.naCommercialUpsells = "true";

    let live = section.querySelector(".na-cart-upsell-status");
    if (!live) {
      live = document.createElement("p");
      live.className = "na-cart-upsell-status";
      live.setAttribute("role", "status");
      live.setAttribute("aria-live", "polite");
      grid.insertAdjacentElement("afterend", live);
    }

    const eyebrow = section.querySelector(".blaze-cart-offers__eyebrow");
    const heading = section.querySelector("h3");
    const intro = heading?.nextElementSibling;
    if (eyebrow) eyebrow.textContent = "Useful Products For This Cart";
    if (heading) {
      heading.textContent = missingRecommendationRoles(state).length
        ? "Complete An Offer Or Add The Next Useful Upgrade."
        : "Your Offers Are Covered. Add The Next Useful Upgrade.";
    }
    if (intro?.tagName === "P") {
      intro.textContent = state.allPairOffersComplete
        ? "The pair offers in this cart are complete. These available products add another practical lighting zone without hiding the offer already earned."
        : "Recommendations prioritise the product role missing from this cart, then available adjacent upgrades. Confirm fitment on the product page when options are required.";
    }
  };

  let cartEnhancementSequence = 0;
  let cartEnhancementController;
  const enhanceCart = async () => {
    const sequence = ++cartEnhancementSequence;
    cartEnhancementController?.abort();
    cartEnhancementController = new AbortController();
    try {
      const response = await fetch(CART_URL, {
        cache: "no-store",
        headers: { Accept: "application/json" },
        signal: cartEnhancementController.signal,
      });
      if (!response.ok) return;
      const cart = await response.json();
      if (sequence !== cartEnhancementSequence) return;
      const cartOffers = document.querySelector(".blaze-cart-offers");
      if (cartOffers) cartOffers.hidden = Number(cart?.item_count || 0) === 0;
      if (!Number(cart?.item_count || 0)) return;
      const state = cartOfferState(cart);
      renderOfferDashboard(cart, state);
      await renderCartUpsells(cart, state);
      if (sequence !== cartEnhancementSequence) return;
      await ensurePopupRecommendations(cart, state);
    } catch (error) {
      if (error?.name === "AbortError") return;
      /* The native cart remains functional if the enhancement request fails. */
    }
  };

  const enhanceProductBuilder = () => {
    const facts = document.querySelector(".section-night-atlas-product-facts");
    const setup = document.querySelector(".section-night-atlas-setup-builder");
    if (facts && setup && setup.parentNode === facts.parentNode) {
      setup.parentNode.insertBefore(facts, setup);
    }

    const builder = document.querySelector("[data-blaze-complete-setup]");
    if (!builder || builder.dataset.naCommercialEnhancement === "true") return;
    builder.dataset.naCommercialEnhancement = "true";

    const roles = String(builder.dataset.blazeSourceRoles || "")
      .split("|")
      .filter(Boolean);
    let heading = "Your Product Is Selected. Choose The Product That Completes It.";
    let summary = "The product you are viewing stays selected. Choose one available companion from the full grid, then add both products to cart in one step.";
    let offer = "Choose one eligible companion. Checkout checks the completed pair automatically and applies the best valid saving.";

    if (roles.includes("WORK_SCENE_B")) {
      heading = "Your Work / Scene Light Is Selected. Choose An LED Bar.";
      summary = "Every available matching LED Bar is shown below—no slider and no hidden options. Select one to add the pair in one step.";
      offer = "Offer: eligible LED Bar + this eligible Work or Scene Light. Checkout applies 10% off one matched Work or Scene Light.";
    } else if (roles.includes("LED_BARS_A")) {
      heading = "Your LED Bar Is Selected. Choose A Work / Scene Light.";
      summary = "Every available matching Work or Scene Light is shown below. Select one to add the pair in one step.";
      offer = "Offer: this eligible LED Bar + one eligible Work or Scene Light. Checkout applies 10% off one matched Work or Scene Light.";
    } else if (roles.includes("FOG_B")) {
      heading = "Your Fog Light Is Selected. Choose A Spotlight.";
      summary = "Every available eligible Spotlight or Driving Light is shown below. Select one to add the pair in one step.";
      offer = "Offer: eligible Spotlight or Driving Light + this eligible Fog Light. Checkout applies 10% off one eligible Fog Light.";
    } else if (roles.includes("SPOTLIGHTS_A")) {
      heading = "Your Spotlight Is Selected. Choose A Fog Light.";
      summary = "Every available eligible Fog Light is shown below. Select one to add the pair in one step.";
      offer = "Offer: this eligible Spotlight or Driving Light + one eligible Fog Light. Checkout applies 10% off one eligible Fog Light.";
    }

    if (!offerRulesActive()) {
      offer = "This product can form an eligible pairing with one of the available matches below. Checkout confirms the offer currently available.";
    }

    const header = builder.querySelector(".blaze-complete-setup__header");
    const eyebrow = header?.querySelector(".blaze-complete-setup__eyebrow");
    const title = header?.querySelector("h2");
    const description = header?.querySelector(":scope > p");
    if (eyebrow) eyebrow.textContent = "Build The Product Offer";
    if (title) title.textContent = heading;
    if (description) description.textContent = summary;

    if (header && !header.querySelector(".blaze-complete-setup__offer-explainer")) {
      const explainer = document.createElement("div");
      explainer.className = "blaze-complete-setup__offer-explainer";
      const icon = document.createElement("span");
      icon.setAttribute("aria-hidden", "true");
      icon.textContent = "%";
      const copy = document.createElement("div");
      const strong = document.createElement("strong");
      strong.textContent = "What you are building";
      const paragraph = document.createElement("p");
      paragraph.textContent = offer;
      copy.append(strong, paragraph);
      explainer.append(icon, copy);
      header.append(explainer);
    }

    builder
      .querySelector(".blaze-complete-setup__products")
      ?.setAttribute("aria-label", "All available companion product options");

    builder.addEventListener(
      "change",
      (event) => {
        const check = event.target.closest?.("[data-blaze-builder-check]");
        if (!check?.checked) return;
        builder
          .querySelectorAll("[data-blaze-builder-check]")
          .forEach((other) => {
            if (other !== check) other.checked = false;
          });
      },
      true,
    );
  };

  const retireVitalsBoughtTogether = () => {
    const targets = [
      "customers who bought this also bought",
      "customers who also bought",
    ];

    document
      .querySelectorAll("h1, h2, h3, h4, h5, h6, [role='heading']")
      .forEach((heading) => {
        const label = normalise(heading.textContent);
        const isBoughtTogetherHeading =
          targets.includes(label) ||
          (label.startsWith("customers who") &&
            label.includes("bought") &&
            label.includes("also"));
        if (!isBoughtTogetherHeading) return;

        let node = heading.parentElement;
        let widget = null;
        for (let depth = 0; node && depth < 9; depth += 1) {
          if (node.matches("main, product-info, body")) break;
          const productLinks = node.querySelectorAll('a[href*="/products/"]').length;
          const addButtons = [...node.querySelectorAll("button")].filter((button) =>
            /add to cart/i.test(button.textContent || ""),
          ).length;
          if (productLinks || addButtons) {
            widget = node;
            break;
          }
          node = node.parentElement;
        }

        if (!widget) widget = heading.parentElement;
        widget?.classList.add("na-retired-merchandising");
        widget?.setAttribute("hidden", "");
        widget?.setAttribute("aria-hidden", "true");
      });
  };

  const directHeaderCartToPage = (event) => {
    const link = event.target.closest?.(".na-header__cart");
    if (!link) return;
    if (
      event.type === "click" &&
      (event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey)
    ) {
      return;
    }
    if (
      event.type === "keydown" &&
      event.key !== " " &&
      event.code !== "Space"
    ) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.assign("/cart");
  };

  const enableCartPageRouting = (root = document) => {
    root.querySelectorAll?.(".na-header__cart").forEach((link) => {
      if (link.dataset.naCartPageRouting === "true") return;
      link.dataset.naCartPageRouting = "true";
      link.addEventListener("click", directHeaderCartToPage, true);
      link.addEventListener("keydown", directHeaderCartToPage, true);
    });
  };

  const addCartUpsell = async (event) => {
    const form = event.target.closest?.("[data-na-cart-upsell-form]");
    if (!form) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    const button = event.submitter || form.querySelector('button[type="submit"]');
    const status = form
      .closest(".blaze-cart-offers__recommendations")
      ?.querySelector(".na-cart-upsell-status");
    const original = button?.textContent || "Add To Cart";
    if (button) {
      button.disabled = true;
      button.textContent = "Adding…";
    }
    if (status) status.textContent = "Adding this product and refreshing your offer progress…";

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: new FormData(form),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          result.description ||
            result.message ||
            "This product could not be added.",
        );
      }
      if (status) status.textContent = "Added. Updating the cart and every offer bar…";
      document.dispatchEvent(
        new CustomEvent("blaze:cart-updated", {
          bubbles: true,
          detail: { source: "cart-page-upsell", result },
        }),
      );
      window.setTimeout(() => window.location.assign("/cart"), 240);
    } catch (error) {
      if (status) {
        status.textContent =
          error?.message ||
          "This product could not be added. Open the product to choose an option.";
      }
      if (button?.isConnected) {
        button.disabled = false;
        button.textContent = original;
      }
    }
  };

  let enhancementTimer;
  const scheduleCommercialRefresh = (delay = 260) => {
    window.clearTimeout(enhancementTimer);
    enhancementTimer = window.setTimeout(enhanceCart, delay);
  };

  const initialiseCommercialCorrection = () => {
    enhanceProductBuilder();
    retireVitalsBoughtTogether();
    enableCartPageRouting();
    scheduleCommercialRefresh(120);

    document.addEventListener("submit", addCartUpsell, true);
    document.addEventListener("blaze:cart-updated", () => {
      scheduleCommercialRefresh(320);
      window.setTimeout(enhanceCart, 950);
    });
    document.addEventListener("cart:updated", () => scheduleCommercialRefresh(120));
    if (
      typeof subscribe === "function" &&
      typeof PUB_SUB_EVENTS !== "undefined" &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleCommercialRefresh(120));
    }
    document.addEventListener("shopify:section:load", (event) => {
      enhanceProductBuilder();
      retireVitalsBoughtTogether();
      enableCartPageRouting(event.target);
      scheduleCommercialRefresh(120);
    });

    const vitalsObserver = new MutationObserver(() => {
      window.requestAnimationFrame(retireVitalsBoughtTogether);
    });
    vitalsObserver.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialiseCommercialCorrection, {
      once: true,
    });
  } else {
    initialiseCommercialCorrection();
  }
})();
